// components/Dashboard/WeeklyHighlightsCard.jsx
import React, { useState } from "react";
import { Text } from "@fluentui/react-components";
import { formatDate } from "../../utils/helpers";

export default function WeeklyHighlightsCard({ records, textEntities = [], style = {} }) {
  const [idx, setIdx] = useState(0);

  // Only weeks that have at least one text field filled
  const weeks = records.filter((r) =>
    textEntities.some((e) => r.fields?.[e.key])
  );

  const total = weeks.length;
  const current = weeks[idx] || null;
  const isLatest = idx === 0;
  const isOldest = idx === total - 1;

  const prev = () => setIdx((i) => Math.min(i + 1, total - 1));
  const next = () => setIdx((i) => Math.max(i - 1, 0));

  return (
    <div style={{
      background: "#fff", borderRadius: 16, padding: 22,
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      border: "1px solid #EDEBE9",
      display: "flex", flexDirection: "column",
      ...style,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#5C2D91,#7B3FC2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>
            📋
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Notes & Highlights</div>
            <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 1 }}>
              {total > 0 ? `${total} week${total !== 1 ? "s" : ""} with notes` : "No notes yet"}
            </div>
          </div>
        </div>

        {/* Navigation */}
        {total > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={prev}
              disabled={isOldest}
              title="Older"
              style={navBtnStyle(isOldest)}
            >‹</button>
            <span style={{ fontSize: 11, color: "#6B7280", minWidth: 40, textAlign: "center" }}>
              {idx + 1} / {total}
            </span>
            <button
              onClick={next}
              disabled={isLatest}
              title="Newer"
              style={navBtnStyle(isLatest)}
            >›</button>
          </div>
        )}
      </div>

      {/* Content */}
      {!current ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 0" }}>
          <Text style={{ color: "#9CA3AF", fontSize: 13 }}>No notes recorded yet.</Text>
        </div>
      ) : (
        <div style={{ flex: 1 }}>
          {/* Week badge + latest pill */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "linear-gradient(135deg,#EBF3FF,#F0F7FF)",
              border: "1px solid #C7E0FF", borderRadius: 8,
              padding: "4px 10px", fontSize: 11, fontWeight: 700, color: "#0052CC",
            }}>
              📅 {formatDate(current.weekEnding)}
            </div>
            {isLatest && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: "#fff",
                background: "linear-gradient(135deg,#107C10,#0B5C0B)",
                borderRadius: 6, padding: "3px 8px",
              }}>
                LATEST
              </span>
            )}
          </div>

          {/* Note fields */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {textEntities.map((e) => {
              const val = current.fields?.[e.key];
              if (!val) return null;
              const lines = String(val).split(/\n/).map((s) => s.trim()).filter(Boolean);
              return (
                <div key={e.id} style={{
                  borderRadius: 10, overflow: "hidden",
                  border: "1px solid #F0F0F0",
                }}>
                  {/* Field label */}
                  <div style={{
                    padding: "6px 12px",
                    background: "linear-gradient(135deg,#5C2D91,#7B3FC2)",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <span style={{ fontSize: 13 }}>{e.icon}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                      {e.name}
                    </span>
                  </div>
                  {/* Field content */}
                  <div style={{ padding: "10px 12px", background: "#FAFBFC" }}>
                    {lines.map((line, i) => (
                      <div key={i} style={{
                        display: "flex", gap: 8, alignItems: "flex-start",
                        marginBottom: i < lines.length - 1 ? 6 : 0,
                      }}>
                        <span style={{ color: "#5C2D91", fontSize: 12, marginTop: 2, flexShrink: 0 }}>•</span>
                        <span style={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>{line}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dot navigation */}
          {total > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 5, marginTop: 14 }}>
              {Array.from({ length: Math.min(total, 7) }).map((_, i) => {
                const dotIdx = total <= 7 ? i : Math.round((i / 6) * (total - 1));
                const isActive = total <= 7 ? i === idx : Math.abs(dotIdx - idx) < 1;
                return (
                  <button
                    key={i}
                    onClick={() => setIdx(total <= 7 ? i : dotIdx)}
                    style={{
                      width: isActive ? 18 : 6, height: 6, borderRadius: 3,
                      border: "none", cursor: "pointer", padding: 0,
                      background: isActive ? "#5C2D91" : "#D1D5DB",
                      transition: "all 0.2s",
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const navBtnStyle = (disabled) => ({
  width: 28, height: 28, borderRadius: 8,
  border: "1.5px solid #E5E7EB",
  background: disabled ? "#F9FAFB" : "#fff",
  color: disabled ? "#D1D5DB" : "#374151",
  fontSize: 16, cursor: disabled ? "default" : "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontFamily: "inherit", transition: "all 0.15s",
  lineHeight: 1,
});